import {
  type CpComparison,
  type CpPerformance,
  type CpTerms,
  type PerformanceReport,
  voyagePerformance,
} from './calc.js';
import {
  type CharterPartyTerms,
  type CpEvent,
  latestCpDecisions,
  type SailingWindow,
  sailingWindows,
  windowRelation,
} from './charter-party.js';
import {
  type CpDay,
  type CpEvaluation,
  cpDays,
  cpEvaluation,
  type LegReport,
} from './good-weather.js';
import type { LoadCondition } from './lists.js';
import type { VoyageReportKind } from './schemas.js';

/**
 * Charter Party Performance analizi (aşama V5; ADR 0084 Ek; ortağın `sefer-4.jpeg`): CP dönemi
 * içindeki seyir pencereleri, ofisin hariç/dahil kararları, iyi hava günleri ve gerçekleşen ile
 * sözleşmenin karşılaştırması (hız, tüketim, seyir süresi). Hesap formülleri değişmez: toplamlar
 * `voyagePerformance`, günler `cpDays`, değerlendirme `cpEvaluation` (ADR 0022 §4, ADR 0024 #16,
 * #17); bu dosya yalnız hangi raporların ve günlerin hesaba gireceğini seçer. Sonuç gemi
 * gözlemine dayanır: "Indicative"; ofis incelerse "Reviewed"; "Contractual" sonuç üretilmez (sahip
 * kararı 2026-10-19, soru 7).
 */

const round = (value: number, digits = 3) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};
const hoursBetween = (from: string, to: string) => (Date.parse(to) - Date.parse(from)) / 3_600_000;
const byTime = (a: { reportedAt: string }, b: { reportedAt: string }) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

/** CP kaydının şartlarından hesap şartları (`cpComparison`). */
export function cpTermsOf(terms: CharterPartyTerms): CpTerms {
  return {
    cpSpeedLadenKn: terms.speedLadenKn,
    cpSpeedBallastKn: terms.speedBallastKn,
    cpConsumptionLadenMt: terms.consumptionLadenMt,
    cpConsumptionBallastMt: terms.consumptionBallastMt,
  };
}

/** Yük durumuna göre CP hızı; bilinmiyorsa laden, o da yoksa ballast (ekranın varsayımı). */
export function cpPlannedSpeed(
  terms: CharterPartyTerms,
  load: LoadCondition | null,
): number | null {
  if (load === 'ballast') return terms.speedBallastKn;
  return terms.speedLadenKn ?? terms.speedBallastKn;
}

export interface CpLeg extends SailingWindow {
  relation: 'inside' | 'partial';
  /** Hesaba giriyor mu: ofisin son kararı, yoksa varsayılan. */
  included: boolean;
  /** İçerideki pencere dahil, sınırı içinde kalan pencere hariç (ADR 0084 §3). */
  defaultIncluded: boolean;
  decision: CpEvent | null;
  windowHours: number;
  excludedHours: number;
  hours: number;
  distanceNm: number;
  avgSpeedKn: number | null;
  consumptionMt: number | null;
  loadCondition: LoadCondition | null;
  /** Ortalama hız CP hızının altında değil; CP hızı ya da hız yoksa `null`. */
  speedOk: boolean | null;
}

export type CpDayExclusion = 'segment' | 'office' | 'short';

export interface CpAnalysisDay extends CpDay {
  decision: CpEvent | null;
  /** Neden hesaba girmiyor: seyir içi aralık, ofis kararı ya da sözleşmenin en kısa günü. */
  exclusion: CpDayExclusion | null;
  /** Ofis günü gerekçeyle dahil etti (iyi hava sayılır). */
  officeIncluded: boolean;
}

export type CpSegmentKind = 'strait' | 'canal' | 'anchorage' | 'drift' | 'waiting';

export interface CpSegmentPeriod {
  kind: CpSegmentKind;
  name: string | null;
  start: string;
  end: string | null;
  hours: number | null;
}

export interface CpExcludedPeriod {
  kind: CpSegmentKind | 'partial_leg' | 'office_leg' | 'office_day';
  name: string | null;
  start: string;
  end: string | null;
  hours: number | null;
  /** Ofis kararının gerekçesi. */
  reason: string | null;
}

export interface CpSeaTime {
  actualHours: number;
  /** Mesafe / CP hızı (yük durumuna göre); hız yoksa `null`. */
  contractualHours: number | null;
  /** Gerçek − sözleşme: artı kaybedilen, eksi kazanılan zaman. */
  differenceHours: number | null;
}

export interface CpAnalysis {
  period: { start: string | null; end: string | null };
  legs: CpLeg[];
  /** Dahil pencerelerin toplamı (`voyagePerformance` CP penceresi); pencere yoksa `null`. */
  performance: CpPerformance | null;
  plannedSpeedKn: number | null;
  days: CpAnalysisDay[];
  evaluation: CpEvaluation | null;
  seaTime: { goodWeather: CpSeaTime | null; whole: CpSeaTime | null };
  excludedPeriods: CpExcludedPeriod[];
}

const OPENS: Partial<Record<VoyageReportKind, CpSegmentKind>> = {
  strait_entry: 'strait',
  canal_entry: 'canal',
  anchorage_start: 'anchorage',
  drift_start: 'drift',
  waiting_start: 'waiting',
};
const CLOSES: Partial<Record<VoyageReportKind, CpSegmentKind>> = {
  strait_exit: 'strait',
  canal_exit: 'canal',
  anchorage_end: 'anchorage',
  drift_end: 'drift',
  waiting_end: 'waiting',
};

/**
 * Seyir içi hariç aralıklar (boğaz, kanal, demir, sürüklenme, bekleme): açılış raporundan
 * kapanışına; kapanış yoksa süre son rapora kadardır. Gönderilmiş raporlar.
 */
export function segmentPeriods(reports: readonly LegReport[]): CpSegmentPeriod[] {
  const sorted = reports.filter((r) => r.status === 'submitted').sort(byTime);
  const out: CpSegmentPeriod[] = [];
  sorted.forEach((report, i) => {
    const kind = OPENS[report.kind];
    if (!kind) return;
    const close = sorted.slice(i + 1).find((r) => CLOSES[r.kind] === kind);
    const last = close ?? sorted.at(-1) ?? report;
    const name = typeof report.data.name === 'string' && report.data.name ? report.data.name : null;
    out.push({
      kind,
      name,
      start: report.reportedAt,
      end: close ? close.reportedAt : null,
      hours: round(hoursBetween(report.reportedAt, last.reportedAt)),
    });
  });
  return out;
}

/** Pencerenin raporları: COSP'tan EOSP'a (yoksa son raporuna) kadar, gönderilmiş. */
function windowReports(sorted: readonly LegReport[], window: SailingWindow): LegReport[] {
  const start = Date.parse(window.start);
  const end = Date.parse(window.end ?? window.lastAt);
  return sorted.filter((r) => {
    const at = Date.parse(r.reportedAt);
    return at >= start && at <= end;
  });
}

const performanceInput = (reports: readonly LegReport[]): PerformanceReport[] =>
  reports.map((r) => ({ kind: r.kind, reportedAt: r.reportedAt, data: r.data }));

function seaTime(
  values: { hours: number; distanceNm: number } | null,
  plannedSpeedKn: number | null,
): CpSeaTime | null {
  if (!values) return null;
  const contractualHours =
    plannedSpeedKn && plannedSpeedKn > 0 ? round(values.distanceNm / plannedSpeedKn) : null;
  return {
    actualHours: round(values.hours),
    contractualHours,
    differenceHours: contractualHours === null ? null : round(values.hours - contractualHours),
  };
}

export interface CpAnalysisInput {
  charterPartyId: string;
  terms: CharterPartyTerms;
  period: { start: string | null; end: string | null };
  /** Geminin raporları (bütün durumlar; yalnız gönderilmişler hesaba girer). */
  reports: readonly LegReport[];
  /** Geminin CP olayları; yalnız bu CP'ninkiler okunur. */
  events: readonly CpEvent[];
}

/**
 * CP analizi: dönemle kesişen pencereler (içeride dahil, kısmen hariç; ofisin son kararı geçer),
 * dahil pencerelerin toplamı, iyi hava günleri (ofisin gün kararı, en kısa gün), gerçekleşen ve
 * sözleşme, hariç dönemler.
 */
export function cpAnalysis(input: CpAnalysisInput): CpAnalysis {
  const { terms, period } = input;
  const sorted = input.reports.filter((r) => r.status === 'submitted').sort(byTime);
  const decisions = latestCpDecisions(input.events, input.charterPartyId);
  const cpTerms = cpTermsOf(terms);
  const legs: CpLeg[] = [];
  for (const window of sailingWindows(sorted)) {
    const relation = windowRelation(window, period);
    if (relation === 'outside') continue;
    const decision = decisions.get(`leg:${window.id}`) ?? null;
    const defaultIncluded = relation === 'inside';
    const included = decision ? decision.action === 'include' : defaultIncluded;
    const own = voyagePerformance(performanceInput(windowReports(sorted, window)), {
      end: null,
    }).cp;
    const planned = cpPlannedSpeed(terms, own?.loadCondition ?? null);
    legs.push({
      ...window,
      relation,
      included,
      defaultIncluded,
      decision,
      windowHours: own?.windowHours ?? 0,
      excludedHours: own?.excludedHours ?? 0,
      hours: own?.hours ?? 0,
      distanceNm: own?.distanceNm ?? 0,
      avgSpeedKn: own?.avgSpeedKn ?? null,
      consumptionMt: own?.consumptionMt ?? null,
      loadCondition: own?.loadCondition ?? null,
      speedOk:
        planned === null || !own || own.avgSpeedKn === null ? null : own.avgSpeedKn >= planned,
    });
  }

  const includedLegs = legs.filter((l) => l.included);
  const includedReports = includedLegs.flatMap((leg) => windowReports(sorted, leg));
  const performance =
    includedLegs.length > 0
      ? voyagePerformance(performanceInput(includedReports), { end: null }).cp
      : null;
  const plannedSpeedKn = cpPlannedSpeed(terms, performance?.loadCondition ?? null);

  const days: CpAnalysisDay[] = performance
    ? cpDays(
        includedReports,
        { start: performance.start, end: performance.end },
        {
          plannedSpeedKn,
          maxBeaufort: terms.maxBeaufort,
          maxWaveHeightM: terms.maxWaveHeightM,
          maxDouglas: terms.maxDouglas,
          maxSwellM: terms.maxSwellM,
        },
      ).map((day) => {
        const decision = decisions.get(`day:${day.id}`) ?? null;
        if (decision?.action === 'include') {
          return {
            ...day,
            goodWeather: true,
            excluded: false,
            decision,
            exclusion: null,
            officeIncluded: true,
          };
        }
        const short =
          terms.minDayHours !== null && day.hours !== null && day.hours < terms.minDayHours;
        const exclusion: CpDayExclusion | null = day.excluded
          ? 'segment'
          : decision?.action === 'exclude'
            ? 'office'
            : short
              ? 'short'
              : null;
        return {
          ...day,
          excluded: exclusion !== null,
          decision,
          exclusion,
          officeIncluded: false,
        };
      })
    : [];
  const evaluation = performance ? cpEvaluation(days, performance, cpTerms) : null;

  const excludedPeriods: CpExcludedPeriod[] = [];
  for (const leg of legs) {
    if (leg.included) continue;
    excludedPeriods.push({
      kind: leg.decision ? 'office_leg' : 'partial_leg',
      name: null,
      start: leg.start,
      end: leg.end,
      hours: round(hoursBetween(leg.start, leg.end ?? leg.lastAt)),
      reason: leg.decision?.reason ?? null,
    });
  }
  for (const segment of segmentPeriods(includedReports)) {
    excludedPeriods.push({ ...segment, reason: null });
  }
  for (const day of days) {
    if (day.exclusion !== 'office') continue;
    const previous = includedReports
      .filter((r) => Date.parse(r.reportedAt) < Date.parse(day.reportedAt))
      .at(-1);
    excludedPeriods.push({
      kind: 'office_day',
      name: null,
      start: previous?.reportedAt ?? day.reportedAt,
      end: day.reportedAt,
      hours: day.hours,
      reason: day.decision?.reason ?? null,
    });
  }
  excludedPeriods.sort((a, b) => Date.parse(a.start) - Date.parse(b.start));

  return {
    period,
    legs,
    performance,
    plannedSpeedKn,
    days,
    evaluation,
    seaTime: {
      goodWeather: evaluation
        ? seaTime(evaluation.goodWeather.days > 0 ? evaluation.goodWeather : null, plannedSpeedKn)
        : null,
      whole: seaTime(performance, plannedSpeedKn),
    },
    excludedPeriods,
  };
}

export interface CpAllowance {
  /** "About" ile en düşük kabul edilen hız. */
  minSpeedKn: number | null;
  /** "About" ile en yüksek kabul edilen günlük tüketim. */
  maxConsumptionMt: number | null;
  speed: boolean | null;
  consumption: boolean | null;
}

/**
 * "About" toleranslı okuma (ADR 0084 Ek): asıl sonuç toleranssızdır (ADR 0024 #16, en sıkı); bu
 * yalnız ek bir satırdır. Tolerans ya da karşılaştırma yoksa `null`.
 */
export function cpAllowance(
  comparison: CpComparison | null,
  terms: CharterPartyTerms,
): CpAllowance | null {
  if (!comparison) return null;
  if (terms.speedToleranceKn === null && terms.consumptionTolerancePct === null) return null;
  const minSpeedKn =
    comparison.plannedSpeedKn === null || comparison.plannedSpeedKn === undefined
      ? null
      : round(comparison.plannedSpeedKn - (terms.speedToleranceKn ?? 0));
  const maxConsumptionMt =
    comparison.plannedConsumptionMt === null || comparison.plannedConsumptionMt === undefined
      ? null
      : round(comparison.plannedConsumptionMt * (1 + (terms.consumptionTolerancePct ?? 0) / 100));
  return {
    minSpeedKn,
    maxConsumptionMt,
    speed:
      minSpeedKn === null ||
      comparison.actualSpeedKn === null ||
      comparison.actualSpeedKn === undefined
        ? null
        : comparison.actualSpeedKn >= minSpeedKn,
    consumption:
      maxConsumptionMt === null ||
      comparison.actualConsumptionMt === null ||
      comparison.actualConsumptionMt === undefined
        ? null
        : comparison.actualConsumptionMt <= maxConsumptionMt,
  };
}

/**
 * Analizin parmak izi (incelemenin hash'i bundan; ADR 0084 §6): dönem, bacakların durumu, günlerin
 * durumu ve değerlendirme. Rapor, karar ya da şart değişirse değişir; aynı girdide aynıdır.
 */
export function cpAnalysisFingerprint(analysis: CpAnalysis): Record<string, unknown> {
  return {
    period: analysis.period,
    legs: analysis.legs.map((l) => ({
      id: l.id,
      included: l.included,
      hours: l.hours,
      distanceNm: l.distanceNm,
      consumptionMt: l.consumptionMt,
    })),
    days: analysis.days.map((d) => ({
      id: d.id,
      goodWeather: d.goodWeather,
      excluded: d.excluded,
      hours: d.hours,
      distanceNm: d.distanceNm,
      consumptionMt: d.consumptionMt,
    })),
    plannedSpeedKn: analysis.plannedSpeedKn,
    evaluation: analysis.evaluation,
  };
}

export type CpResultLabel = 'indicative' | 'reviewed';

/**
 * Sonucun etiketi (sahip kararı 2026-10-19, soru 7): bu CP'nin son incelemesi bugünkü analizin
 * hash'ini taşıyorsa "Reviewed", yoksa "Indicative" (inceleme vardı ama analiz değiştiyse
 * `outdated`). "Contractual" yoktur.
 */
export function cpResultLabel(
  events: readonly CpEvent[],
  charterPartyId: string,
  analysisHash: string,
): { label: CpResultLabel; review: CpEvent | null; outdated: boolean } {
  const review =
    [...events]
      .filter((e) => e.charterPartyId === charterPartyId && e.action === 'review')
      .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt) || (a.id < b.id ? -1 : 1))
      .at(-1) ?? null;
  if (!review) return { label: 'indicative', review: null, outdated: false };
  return review.contentHash === analysisHash
    ? { label: 'reviewed', review, outdated: false }
    : { label: 'indicative', review, outdated: true };
}
